import { useBrowserSupport } from '@/shared/composables/browser-api'
import { useLocalization } from '@/shared/composables/localization'
import { tauriMode } from '@/shared/utils/import-meta'

interface HTMLMediaElementOverrideSetSinkID {
  setSinkId?: (deviceId: string) => Promise<void>
}
export type HTMLAudioElementWithSetSinkId = Modify<HTMLMediaElement, HTMLMediaElementOverrideSetSinkID>

let sharedAudioContext: AudioContext | undefined
const outputSources = new Map<HTMLMediaElement, MediaElementAudioSourceNode>()

export const releaseAudioOutput = (mediaElement: HTMLMediaElement): void => {
  const source = outputSources.get(mediaElement)
  if (!source) return

  try {
    source.disconnect()
  } catch {
    // already disconnected
  }
  outputSources.delete(mediaElement)
}

export function useSetAudioOutput (mediaElement: HTMLAudioElementWithSetSinkId | HTMLMediaElement, deviceId: string): Promise<boolean> {
  return new Promise(async (resolve, reject) => {
    if (tauriMode()) {
      try {
        const element = mediaElement as HTMLMediaElement
        if (!sharedAudioContext) {
          sharedAudioContext = new AudioContext()
        }
        if (sharedAudioContext.state === 'suspended') {
          await sharedAudioContext.resume()
        }
        if (!outputSources.has(element)) {
          const source = sharedAudioContext.createMediaElementSource(element)
          source.connect(sharedAudioContext.destination)
          outputSources.set(element, source)
        }
        resolve(true)
      } catch (error) {
        reject(error)
      }
    } else {
      const { canUseSetAudioOutput } = useBrowserSupport()
      const { t } = useLocalization()
      const isBrowserSupport = await canUseSetAudioOutput(mediaElement as HTMLAudioElementWithSetSinkId)

      if (!isBrowserSupport) { reject(new Error(t('common.browserNotSupportSetSinkId'))) }

      (mediaElement as HTMLAudioElementWithSetSinkId).setSinkId?.(deviceId).then(() => {
        resolve(true)
      }).catch((error) => reject(error))
    }
  })
}


type Modify<T, R> = Pick<T, Exclude<keyof T, keyof R>> & R
